import { AfterViewInit, Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';

import { NGXLogger } from 'ngx-logger';

import { TableActionColumnDef, TableColumnDef, TableFooterColumnDef, TableFooterRowDef } from '@models/data-table.model';
import { TableActionType } from '@models/table-action-type.enum';

import { PagedResponse } from '@models/commons/paged-response.model';
import { QueryConfig } from '@app/shared/utils/query-config.utils';

import { HighlightConfig } from '@models/highlight-config.model';

import { environment } from 'environments/environment';

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements OnChanges, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() data: PagedResponse<any> | any[];
  @Input() config: QueryConfig;
  @Input() imageColumn: string;
  @Input() columns: TableColumnDef[];
  @Input() actionColumns: TableActionColumnDef[];
  @Input() footerRows: TableFooterRowDef[];
  @Input() highlightConfig: HighlightConfig;
  @Input() nextItemsLoading: boolean;
  @Input() pageSizeOptions = [10, 20, 50, 100];
  @Input() hasAvatar = true;
  @Input() showIndex = false;
  @Input() hasTooltip = false;
  @Input() hasPagination = false;

  @Input() highlightFn: (element: any) => boolean;

  @HostBinding('class.compact') @Input() compact = false;

  @Output() recordSelected = new EventEmitter<any>();
  @Output() recordAction = new EventEmitter<any>();
  @Output() sortChange = new EventEmitter<Sort>();
  @Output() pageChange = new EventEmitter<PageEvent>();

  displayedColumns = [];
  displayedActionColumns = [];

  dataSource: MatTableDataSource<any>;

  currentLang = environment.defaultLanguage;

  constructor(private translateService: TranslateService, private logger: NGXLogger) {
    this.currentLang = this.translateService.currentLang;
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.currentLang = event.lang;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.columns) {
      this.displayedColumns = [];
      this.columns.forEach(column => {
        this.displayedColumns.push(column.value);
      });
    }
    if (changes.actionColumns) {
      this.displayedActionColumns = [];
      this.actionColumns.forEach(column => {
        this.displayedActionColumns.push(column.action);
      });
    }
    if (changes.data && this.data) {
      if (Array.isArray(this.data)) {
        if (this.showIndex) {
          this.data = this.data.map((element, index) => new Object({
            index: this.elementIndex(index),
            ...element
          }));
        }
        this.dataSource = new MatTableDataSource(this.data);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
      } else {
        this.dataSource = new MatTableDataSource(this.data.items);
      }
    }
  }

  ngAfterViewInit() {
    if (Array.isArray(this.data)) {
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    }
  }

  translateHeaderName(columnKey) {
    return columnKey ? this.translateService.instant(columnKey) : '';
  }

  onSelect(record) {
    this.logger.info('onClick:: Item was selected: ', record);
    this.recordSelected.emit(record);
  }

  onAction(record, type: TableActionType) {
    this.logger.info('onClick:: Item action: ', record, type);
    this.recordAction.emit({record, type});
  }

  onSortChange(sort: Sort) {
    if (sort.active === '') {
      return;
    }
    this.sortChange.emit(sort);
  }

  onPageChange(pageEvent: PageEvent) {
    this.pageChange.emit(pageEvent);
  }

  setContent(column, element, index) {
    return {column, element, index};
  }

  clearSort() {
    this.sort.sort({id: '', start: 'asc', disableClear: false});
  }

  cellValue(element: any, column: TableColumnDef): string {
    if (column.format) {
      return column.format(element);
    }
    return element[column.value];
  }

  elementIndex(index: number): number {
    return (this.config?.pager.page * this.config?.pager.pageSize) + index + 1;
  }

  shouldHighlightRow(row: any): boolean {
    return this.highlightConfig && row[this.highlightConfig.column] === this.highlightConfig.value;
  }

  matDefinedFooterColumns(footerRow: TableFooterRowDef): string[] {
    return footerRow.columns.map((column) => column.value);
  }

  footerCellValue(column: TableFooterColumnDef): string {
    if (this.isDataArray) {
      return '';
    }
    if (column.format) {
      return column.format();
    }
    return this.data[column.value]?.toString() || '';
  }

  get matDefinedColumns() {
    return [
      ...this.imageColumn ? ['image'] : [],
      ...this.showIndex ? ['index'] : [],
      ...this.displayedColumns,
      ...this.displayedActionColumns
    ];
  }

  get highlightColor(): string {
    return this.highlightConfig?.color;
  }

  get dataLength(): number {
    return (Array.isArray(this.data) ? this.data?.length : this.data?.total) || 0;
  }

  get isDataArray(): boolean {
    return Array.isArray(this.data);
  }
}
